import React from "react";
import Select, {
	components,
	type OptionProps,
	type SingleValueProps,
	type GroupBase,
	MenuPlacement,
} from "react-select";

export interface IUserOption {
	value: string;
	name: string;
	isRelieved: boolean;
	staffId?: string;
	email?: string;
}

function CheckIcon() {
	return (
		<svg
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth={2.5}
			strokeLinecap="round"
			strokeLinejoin="round"
			className="h-4 w-4 shrink-0 text-blue-600"
		>
			<polyline points="4 10 8 14 16 6" />
		</svg>
	);
}

function Option(props: OptionProps<IUserOption, false>) {
	const { name, email } = props.data;
	const { isSelected } = props;
	return (
		<components.Option {...props}>
			<div className="min-w-0 flex-1">
				<div className="flex items-baseline justify-between gap-3">
					<p
						className={`truncate text-[13.5px] font-medium ${
							isSelected ? "text-blue-600" : "text-slate-900"
						}`}
					>
						{name}
					</p>
				</div>
				<div className="mt-0.5 flex items-center justify-between gap-3">
					<span className="shrink-0 text-[11px] font-medium text-slate-400">
						Email {email}
					</span>
					{isSelected && <CheckIcon />}
				</div>
			</div>
		</components.Option>
	);
}

function SingleValue(props: SingleValueProps<IUserOption, false>) {
	const { name } = props.data;
	return (
		<components.SingleValue {...props}>
			<div
				title={name}
				className="flex min-w-0 items-baseline gap-2  max-w-[300px] truncate"
			>
				<span className="truncate text-sm font-medium text-slate-800">
					{name}
				</span>
				{/* Display operation role if available */}
				{/* {operationRole && (
					<span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full truncate">
						{operationRole.join(', ')}
					</span>
				)} */}
			</div>
		</components.SingleValue>
	);
}

const classNames = {
	control: ({
		isFocused,
		isDisabled,
	}: {
		isFocused: boolean;
		isDisabled?: boolean;
	}) =>
		[
			"flex items-center rounded-md border bg-white px-3 py-1 text-sm shadow-sm transition-all duration-150 min-w-full sm:min-w-[256px] md:min-w-[300px] max-w-[300px]",
			isDisabled
				? "cursor-not-allowed border-slate-100 bg-slate-50 opacity-60"
				: isFocused
					? "border-blue-500 ring-2 ring-blue-100"
					: "border-slate-200 hover:border-slate-300",
		].join(" "),
	valueContainer: () => "gap-1.5 px-1 py-1",
	placeholder: () => "text-slate-400",
	input: () => "text-sm text-slate-800",
	indicatorsContainer: () => "gap-1",
	dropdownIndicator: () => "text-slate-400 hover:text-slate-600 px-1",
	clearIndicator: () => "text-slate-400 hover:text-slate-600 px-1",
	indicatorSeparator: () => "bg-slate-200",
	menu: () =>
		"mt-2 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg shadow-slate-900/10",
	menuList: () =>
		"flex flex-col gap-0.5 p-1.5 max-h-48 overflow-y-auto overflow-x-hidden ws-scroll",
	option: ({
		isFocused,
		isSelected,
		isDisabled,
	}: {
		isFocused: boolean;
		isSelected: boolean;
		isDisabled?: boolean;
	}) =>
		[
			"flex cursor-pointer items-center gap-2 border-l-[3px] py-2.5 pl-3 pr-3.5 transition-colors duration-100 active:bg-blue-100",
			isDisabled && "pointer-events-none cursor-not-allowed opacity-50",
			isSelected
				? "border-l-blue-500 bg-blue-100"
				: isFocused
					? "border-l-transparent bg-blue-100"
					: "border-l-transparent bg-white hover:bg-slate-100",
		]
			.filter(Boolean)
			.join(" "),
	noOptionsMessage: () => "py-8 text-center text-sm text-slate-400",
};

interface UserSelectProps {
	options: IUserOption[];
	value?: IUserOption | null;
	onChange?: (option: IUserOption | null) => void;
	placeholder?: string;
	menuPlacement?: MenuPlacement | undefined;
	isDisabled?: boolean;
}

export default function UserSelect({
	options,
	value,
	onChange,
	placeholder = "Select a user",
	menuPlacement,
	isDisabled,
}: UserSelectProps) {
	return (
		<Select<IUserOption, false, GroupBase<IUserOption>>
			unstyled
			options={options}
			value={value}
			onChange={(opt) => onChange?.(opt ?? null)}
			placeholder={placeholder}
			isClearable
			menuPlacement={menuPlacement}
			classNames={classNames}
			components={{ Option, SingleValue }}
			menuPosition="fixed"
			isLoading={isDisabled}
			isDisabled={isDisabled}
			menuPortalTarget={
				typeof document !== "undefined" ? document.body : undefined
			}
			styles={{ menuPortal: (base) => ({ ...base, zIndex: 50 }) }}
			getOptionLabel={(opt) => opt.name}
			getOptionValue={(opt) => opt.value}
			filterOption={(option, rawInput) => {
				const input = rawInput.toLowerCase().trim();
				if (!input) return true;
				const { name, email } = option.data;
				return (
					(name?.toLowerCase().includes(input) ?? false) ||
					(email?.toLowerCase().includes(input) ?? false)
				);
			}}
		/>
	);
}
